import Cookies from 'universal-cookie'
import { crossSiteCookieOptions, writeCookie, expireCookie } from './embedded-cookies'

/**
 * Amplify auth storage for the Learnforce iframe embed (PENG-3889).
 *
 * Amplify normally builds its own storage from the `cookieStorage` config, which
 * instantiates CookieStorage from amazon-cognito-identity-js. That class copies
 * only path/expires/domain/secure/sameSite into the cookie it writes, so there is
 * no way to ask it for the Partitioned attribute the embedded app needs. Amplify
 * does however accept a `storage` object outright (and prefers it over
 * `cookieStorage`), which is what this module provides.
 *
 * Cookies stay the storage medium on purpose. localStorage would work in the
 * frame, but reducers/user/slice.js reads the Cognito idToken straight out of
 * document.cookie to mint Nova.CurrentSession, and the CloudFront signed cookies
 * have to travel on the wire — neither survives a move off cookies.
 */

const cookies = new Cookies()

// Matches the expiry Amplify's own cookieStorage config has always used.
const EXPIRY_DAYS = 365

const DAY_IN_MS = 24 * 60 * 60 * 1000

/**
 * Keys Amplify and amazon-cognito-identity-js own. Used only by clear(), to keep
 * it from evicting unrelated cookies the way CookieStorage.clear() does.
 */
const isAuthKey = key =>
  key.startsWith('CognitoIdentityServiceProvider') || key.startsWith('amplify-')

const writeOptions = () => ({
  path: '/',
  domain: window.location.hostname,
  expires: new Date(Date.now() + EXPIRY_DAYS * DAY_IN_MS),
  secure: true,
  ...crossSiteCookieOptions()
})

// A cookie is only overwritten or expired by a write whose attributes match, so
// removal has to repeat everything except the lifetime.
const removeOptions = () => ({
  path: '/',
  domain: window.location.hostname,
  secure: true,
  ...crossSiteCookieOptions()
})

const embeddedAuthStorage = {
  setItem (key, value) {
    writeCookie(key, value, writeOptions())
    return value
  },

  /**
   * doNotParse is essential: universal-cookie otherwise JSON.parses any value
   * that opens with { [ or ", and Amplify stores userData as a JSON string that
   * it expects to get back as a string.
   */
  getItem (key) {
    return cookies.get(key, { doNotParse: true })
  },

  removeItem (key) {
    expireCookie(key, removeOptions())
  },

  clear () {
    Object.keys(cookies.getAll({ doNotParse: true }))
      .filter(isAuthKey)
      .forEach(key => expireCookie(key, removeOptions()))
    return {}
  }
}

export default embeddedAuthStorage
export { isAuthKey }
